import { ContentMetadataStoreEntity, VideoFeed, VideoFeedType, VideoSource, TimeSpan } from '../types/video-feed-types';
import { SpeakerWithDetails, Pronouns } from '../types/speaker-types';
import { screenShareBarsColors } from '../constants/speakerColors';
import { SpeakersAnalysis } from '../types/speakers-analysis';
import { AlignedRecordings } from '../types/clip-types';
import { isEntityScreenshare, isEntitySharedVideo } from './activityUtils';
import { ORIGINAL_VIDEO_ID, SCREENSHARE_VIDEO_ID } from '../constants/screenshare';
import { buildSpeakerSlotsFromTranscript } from './speakerSlots';
import { buildScreenshareSpeakerDetails, buildSharedVideoSpeakerDetails } from './speakerDetailsBuilders';
import { buildCropInfoForSpeaker, buildStageTimeSpansFromAnalysis } from './cropAnalysis';
import { isLayoutsWithVisionDataEnabledForSession } from './mediaContentUtils';
import { buildSpeakerSpeakingSlots, getSharedVideoSubtractionContext } from './speakerSlotNormalization';

const getVideoFeedTypeFromEntity = (entity: AlignedRecordings['entity']): VideoFeedType => {
  if (isEntityScreenshare(entity)) {
    return VideoFeedType.SCREENSHARE;
  }
  if (isEntitySharedVideo(entity)) {
    return VideoFeedType.SHARED_VIDEO;
  }
  return VideoFeedType.USER_FEED;
};

const normalizeUrl = (url: string): string => {
  return url.replace(/([^:])\/\//g, '$1/');
};

const constructOriginalVideoFeed = (originalVideoSource: VideoSource, maxVideoDuration?: number): VideoFeed => {
  const stageTimeSpans =
    typeof maxVideoDuration === 'number' && Number.isFinite(maxVideoDuration) && maxVideoDuration >= 0
      ? [{ start: 0, end: maxVideoDuration }]
      : [];

  return {
    id: ORIGINAL_VIDEO_ID,
    type: VideoFeedType.ORIGINAL,
    speakerKey: '',
    stageTimeSpans,
    speakerSlot: [],
    source: originalVideoSource,
    cropInfoOverTime: []
  };
};

const constructVideoFeedsFromAlignedRecordings = (
  alignedRecordings: AlignedRecordings[],
  speakerDetails: Record<string, SpeakerWithDetails>,
  speakerSlotsFromTranscript: Record<string, TimeSpan[]>
): VideoFeed[] => {
  return alignedRecordings.map((recording, index) => {
    const type = getVideoFeedTypeFromEntity(recording.entity);
    const speakerKey = (recording.transcript_mapping as Record<string, string[]>)?.speaker_labels?.[0] ?? '';

    let speaker: SpeakerWithDetails | undefined;
    const stageSpans = (recording.stage_time_spans || []) as Array<{ start_time: number; end_time: number }>;
    if (type === VideoFeedType.SCREENSHARE) {
      speaker = (speakerKey ? speakerDetails[speakerKey] : undefined) ?? buildScreenshareSpeakerDetails(stageSpans);
    } else if (type === VideoFeedType.SHARED_VIDEO) {
      speaker = (speakerKey ? speakerDetails[speakerKey] : undefined) ?? buildSharedVideoSpeakerDetails(stageSpans);
    } else {
      speaker = speakerKey ? speakerDetails[speakerKey] : undefined;
    }

    const stageTimeSpans = (recording.stage_time_spans || []).map(span => ({
      start: span.start_time,
      end: span.end_time
    }));

    const speakerSlot: TimeSpan[] = speakerSlotsFromTranscript[speakerKey] ?? [];

    const videoMetadata = recording.video_metadata as { width?: number | null; height?: number | null } | undefined;
    const aspectRatio = videoMetadata ? videoMetadata.width! / videoMetadata.height! : undefined;

    return {
      id: recording.id,
      type,
      speakerKey,
      stageTimeSpans,
      speakerSlot,
      source: {
        src: normalizeUrl(recording.mp4_url),
        preview: recording.preview_url ?? '',
        altText: 'Video ' + recording.entity + index
      },
      cropInfoOverTime: [],
      speakerDetails: speaker,
      aspectRatio
    };
  });
};

const constructScreenshareFeedFromSpeakerAnalysis = (
  screenshare_bounds: SpeakersAnalysis['screenshare_bounds'],
  originalVideoSource: VideoSource
): VideoFeed | null => {
  if (!screenshare_bounds || screenshare_bounds.length === 0) {
    return null;
  }

  const screenshareSpeakerDetails: SpeakerWithDetails = {
    first_name: 'Shared Screen',
    last_name: '',
    id: 'screen-share',
    key: 'screen-share',
    color: screenShareBarsColors.textColor,
    bgColor: screenShareBarsColors.bgColor,
    lightBgColor: screenShareBarsColors.lightBgColor,
    hoverBgColor: screenShareBarsColors.hoverBgColor,
    speakingSlots: Object.fromEntries(screenshare_bounds.map(bound => [String(bound.start_time), bound.end_time])),
    parent_id: '',
    company: '',
    title: '',
    profile_picture_url: '',
    pronouns: Pronouns.HE
  };

  const screenshareStageTimeSpans = screenshare_bounds.map(bound => ({
    start: bound.start_time,
    end: bound.end_time
  }));
  const screenshareCropInfoOverTime = screenshare_bounds.map(bound => ({
    start: bound.start_time,
    end: bound.end_time,
    cropInfo: {
      top_left: {
        x: bound.coordinates.x,
        y: bound.coordinates.y
      },
      bottom_right: {
        x: bound.coordinates.x + bound.coordinates.width,
        y: bound.coordinates.y + bound.coordinates.height
      }
    }
  }));

  return {
    id: SCREENSHARE_VIDEO_ID,
    type: VideoFeedType.SCREENSHARE,
    speakerKey: '',
    stageTimeSpans: screenshareStageTimeSpans,
    speakerSlot: [],
    source: { ...originalVideoSource, altText: 'Screenshare' },
    cropInfoOverTime: screenshareCropInfoOverTime,
    speakerDetails: screenshareSpeakerDetails
  };
};

const constructVideoFeedsFromSpeakerAnalysis = (
  speakerDetails: Record<string, SpeakerWithDetails>,
  speakerAnalysis: SpeakersAnalysis,
  originalVideoSource: VideoSource,
  speakerSlotsFromTranscript: Record<string, TimeSpan[]>,
  startTime: number,
  endTime: number
): VideoFeed[] => {
  const feeds: VideoFeed[] = [];
  const { speaker_mapping } = speakerAnalysis;

  const lastTime = endTime;
  const faceIdToSpeaker: Record<number, SpeakerWithDetails> = {};
  Object.entries(speaker_mapping).forEach(([speakerId, faceIds]) => {
    const speaker = Object.values(speakerDetails).find(s => s.id === speakerId);
    if (speaker) {
      faceIds.forEach(faceId => {
        faceIdToSpeaker[faceId] = speaker;
      });
    }
  });

  const speakerKeyToFaceIds: Record<string, number[]> = {};
  Object.entries(faceIdToSpeaker).forEach(([faceIdStr, speaker]) => {
    const faceId = Number(faceIdStr);
    if (!speakerKeyToFaceIds[speaker.key]) {
      speakerKeyToFaceIds[speaker.key] = [];
    }
    speakerKeyToFaceIds[speaker.key].push(faceId);
  });

  Object.entries(speakerDetails).forEach(([_, speaker]) => {
    const faceIds = speakerKeyToFaceIds[speaker.key] ?? [];
    const hasFaceData = faceIds.length > 0;

    const speakerSlot: TimeSpan[] = speakerSlotsFromTranscript[speaker.key] ?? [];

    const stageTimeSpans = hasFaceData
      ? buildStageTimeSpansFromAnalysis(faceIds, speakerAnalysis, lastTime)
      : speakerSlot.map(slot => ({ start: slot.start, end: slot.end }));
    const cropInfoOverTime = hasFaceData ? buildCropInfoForSpeaker(faceIds, speakerAnalysis, lastTime) : [];

    feeds.push({
      id: speaker.key,
      type: VideoFeedType.USER_FEED,
      speakerKey: speaker.key,
      stageTimeSpans,
      speakerSlot,
      source: { ...originalVideoSource, altText: `Video ${speaker.key}` },
      cropInfoOverTime,
      speakerDetails: speaker
    });
  });

  return feeds;
};

const constructVideoFeedsFromSpeakerDetails = (
  speakerDetails: Record<string, SpeakerWithDetails>,
  originalVideoSource: VideoSource,
  speakerSlotsFromTranscript: Record<string, TimeSpan[]>
): VideoFeed[] => {
  const feeds: VideoFeed[] = [];

  Object.entries(speakerDetails).forEach(([_, speaker]) => {
    const speakerSlot: TimeSpan[] = speakerSlotsFromTranscript[speaker.key] ?? [];
    const stageTimeSpans = speakerSlot.map(slot => ({ start: slot.start, end: slot.end }));

    feeds.push({
      id: speaker.key,
      type: VideoFeedType.USER_FEED,
      speakerKey: speaker.key,
      stageTimeSpans,
      speakerSlot,
      source: { ...originalVideoSource, altText: `Video ${speaker.key}` },
      cropInfoOverTime: [],
      speakerDetails: speaker
    });
  });

  return feeds;
};

const hasSpeakerAnalysisData = (speakerAnalysis?: SpeakersAnalysis): boolean => {
  const hasSpeakerMapping =
    !!speakerAnalysis?.speaker_mapping && Object.keys(speakerAnalysis.speaker_mapping).length > 0;
  const hasScreenshareBounds = !!speakerAnalysis?.screenshare_bounds && speakerAnalysis.screenshare_bounds.length > 0;
  return hasSpeakerMapping || hasScreenshareBounds;
};

export const constructVideoFeeds = ({
  contentMetadataStoreEntry,
  areLayoutsEnabledForPreRecordedEvents
}: {
  contentMetadataStoreEntry: ContentMetadataStoreEntity;
  areLayoutsEnabledForPreRecordedEvents: boolean;
}): VideoFeed[] => {
  const { metadata } = contentMetadataStoreEntry;
  const { alignedRecordings, speakerDetails, speakerAnalysis, transcript, sessionDetails } = metadata;

  const {
    contentMediaSource,
    isIndividualRecordingComplete,
    eventType,
    broadcastType,
    originalVideoSource,
    maxVideoDuration,
    startTime,
    endTime
  } = sessionDetails ?? {};
  const feeds: VideoFeed[] = [];

  const transcriptWords = transcript ?? [];
  const safeAlignedRecordings = alignedRecordings ?? [];
  const { sharedVideoSpeakers, mergedSharedVideoRanges } = getSharedVideoSubtractionContext(safeAlignedRecordings);

  const isRecordingStudio = eventType === 'Recording';
  const isLiveEventWithAlignedStream =
    contentMediaSource === 'RECORDING' &&
    !isRecordingStudio &&
    broadcastType === 'LIVE_HYBRID' &&
    isIndividualRecordingComplete &&
    safeAlignedRecordings.length > 0;
  const isLayoutsWithVisionData = isLayoutsWithVisionDataEnabledForSession({
    contentMediaSource,
    eventType,
    broadcastType,
    startTime,
    endTime,
    maxVideoDuration,
    areLayoutsEnabledForPreRecordedEvents
  });
  const isLayoutsWithVisionDataAndFacialRec = isLayoutsWithVisionData && hasSpeakerAnalysisData(speakerAnalysis);

  const isLoading =
    contentMetadataStoreEntry.loading.clipDataLoaded ||
    contentMetadataStoreEntry.loading.transcriptLoading ||
    (isLayoutsWithVisionData && contentMetadataStoreEntry.loading.speakerAnalysisLoading);
  if (isLoading) {
    return contentMetadataStoreEntry.videoFeeds;
  }

  const speakerSlotsFromTranscript = buildSpeakerSlotsFromTranscript(transcriptWords);

  const safeSpeakerDetails = Object.entries(speakerDetails ?? {}).reduce(
    (acc, [entryKey, details]) => {
      const speakerKey = details.key || entryKey;
      const transcriptSlots = speakerSlotsFromTranscript[speakerKey] ?? [];
      const speakingSlotsFromTranscript = buildSpeakerSpeakingSlots({
        speakerKey,
        transcriptSlots,
        fallbackSpeakingSlots: details.speakingSlots,
        sharedVideoSubtractionContext: { sharedVideoSpeakers, mergedSharedVideoRanges }
      });

      acc[speakerKey] = {
        ...details,
        key: speakerKey,
        speakingSlots: speakingSlotsFromTranscript
      };
      return acc;
    },
    {} as Record<string, SpeakerWithDetails>
  );

  const videoSource: VideoSource | undefined = originalVideoSource
    ? { preview: originalVideoSource.preview, src: originalVideoSource.source, altText: '' }
    : undefined;

  if (isLiveEventWithAlignedStream) {
    feeds.push(
      ...constructVideoFeedsFromAlignedRecordings(safeAlignedRecordings, safeSpeakerDetails, speakerSlotsFromTranscript)
    );
    if (videoSource) {
      feeds.push(constructOriginalVideoFeed(videoSource, maxVideoDuration));
    }
  } else if (isRecordingStudio) {
    feeds.push(
      ...constructVideoFeedsFromAlignedRecordings(safeAlignedRecordings, safeSpeakerDetails, speakerSlotsFromTranscript)
    );
  } else {
    if (
      isLayoutsWithVisionDataAndFacialRec &&
      videoSource &&
      speakerAnalysis &&
      startTime !== undefined &&
      endTime !== undefined
    ) {
      feeds.push(
        ...constructVideoFeedsFromSpeakerAnalysis(
          safeSpeakerDetails,
          speakerAnalysis,
          videoSource,
          speakerSlotsFromTranscript,
          startTime,
          endTime
        )
      );
    } else if (videoSource) {
      feeds.push(...constructVideoFeedsFromSpeakerDetails(safeSpeakerDetails, videoSource, speakerSlotsFromTranscript));
    }

    if (videoSource) {
      feeds.push(constructOriginalVideoFeed(videoSource, maxVideoDuration));
    }

    if (isLayoutsWithVisionDataAndFacialRec && videoSource && speakerAnalysis) {
      const screenshareFeed = constructScreenshareFeedFromSpeakerAnalysis(
        speakerAnalysis.screenshare_bounds,
        videoSource
      );
      if (screenshareFeed) {
        feeds.push(screenshareFeed);
      }
    }
  }
  return feeds;
};
